import { Injectable } from '@angular/core';
import { DatabaseService } from './database.service';
import { ConnectionService } from '../connection/connection.service';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private fileApiUrl = 'https://kraken.ibexfusion.com/get/';
  private shortUrlApiUrl = 'https://s.ipeak.info/new/';
  private shortUrlApiKey = 'dXQNUehu39BNdwNtaLkhwQKP6Q8tYQzY';
  blobUrlStorage: any = {};

  constructor(private connection: ConnectionService, private db: DatabaseService) {
  }

  public getImage(url: string, forceLive = false, forceSave = false) {
    return this.getValue(url, 'image', forceLive, forceSave);
  }

  public getVideo(url: string, forceLive = false, forceSave = false, wait = false) {
    return this.getValue(url, 'video', forceLive, forceSave, wait);
  }

  public getShortUrl(url) {
    return this.getValue(url, 'shortUrl');
  }

  convertUrl(url: string, forceLive = false) {
    if (forceLive) {
      return url;
    }
    if (!!url && url?.startsWith('http') && url.indexOf('tweebie-staging/assets') === -1 && url.indexOf('tweebie-staging.s3') === -1 && url.indexOf('https://ibexfusion5.s3') === -1) {
      return this.fileApiUrl + this.getBase64(url);
    }
    return url;
  }

  private async getValue(url: string, type = 'image', forceLive = false, forceSave = false, wait = false): Promise<string> {
    if (!url) {
      return Promise.resolve(null);
    }
    if (type === 'image' || type === 'video') {
      if (!url?.startsWith('http')) {
        if (!forceSave) {
          return Promise.resolve(url);
        }
      }
    }
    let result;
    try {
      result = await this.db.get(this.convertUrl(url, true));
      if (!result) {
        result = await this.db.get(this.convertUrl(url, false));
      }
    } catch (err) {
      console.error('error: ', err);
      return url;
    }
    if (result) {
      if (type === 'image') {
        if (this.blobUrlStorage[url]) {
          return this.blobUrlStorage[url];
        }
        const blobUrl = window.URL.createObjectURL(new Blob([result]));
        this.blobUrlStorage[url] = blobUrl;
        return blobUrl;
      } else if (type === 'shortUrl') {
        return result;
      } else if (type === 'video') {
        if (typeof result === 'string') {
          try {
            console.warn('removing old video from db');
            this.db.remove(this.convertUrl(url, forceLive));
            const videoBlob = this.base64ToBlob(result, 'video/mp4');
            if (this.blobUrlStorage[url]) {
              return this.blobUrlStorage[url];
            }
            const blobUrl = window.URL.createObjectURL(videoBlob);
            this.blobUrlStorage[url] = blobUrl;
            return blobUrl;
          } catch (err) {
            console.warn(err);
          }
        }
        if (this.blobUrlStorage[url]) {
          return this.blobUrlStorage[url];
        }
        const blobUrl = window.URL.createObjectURL(new Blob([result]));
        this.blobUrlStorage[url] = blobUrl;
        return blobUrl;
      }
    }
    if (type === 'image') {
      let tempUrl = this.convertUrl(url, forceLive);
      this.cacheImage(tempUrl);
      return tempUrl;
    } else if (type === 'video') {
      let tempUrl = this.convertUrl(url, forceLive);
      let urlsInProgress = JSON.parse(localStorage.getItem('urlsInProgress'));
      if (urlsInProgress && urlsInProgress.includes(url)) {
        return;
      } else {
        if (!urlsInProgress) {
          urlsInProgress = [];
        }
        urlsInProgress.push(url);
        localStorage.setItem('urlsInProgress', JSON.stringify(urlsInProgress));
      }
      if (wait) {
        await this.cacheVideo(url);
        return this.getValue(url, type, forceLive, forceSave);
      } else {
        this.cacheVideo(url);
        return tempUrl;
      }
    } else if (type === 'shortUrl') {
      try {
        const result = await this.connection.getSimple(this.shortUrlApiUrl + encodeURIComponent(url) + '/' + this.shortUrlApiKey);
        let parsedResult = !!result['short_link'] ? result : (typeof result === 'string' ? JSON.parse((<any>result).text()) : null);
        if (!parsedResult['short_link']) {
          return null;
        }
        let shortUrl = parsedResult['short_link'];
        try {
          await this.db.put(this.convertUrl(url), shortUrl);
          return shortUrl;
        } catch (err) {
          console.error('error: ', err);
          return shortUrl;
        }
      } catch (createShortUrlError) {
        console.error('error: ', createShortUrlError);
      }
      return url;
    }
  }

  private async cacheImage(url) {
    let blob;
    try {
      blob = await fetch(url, { method: 'GET' }).then(res => res.blob());
    } catch (err) {
      console.warn('could not get image with url: ', url, err);
    }
    let dataInDb;
    try {
      dataInDb = await this.db.put(url, blob);
    } catch (err) {
      console.warn('could not put item to image db', url, err);
    }
  }

  async downloadLargeMkvToBlob(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Upload error: ${response.statusText}`);

      const reader = response.body.getReader();
      const chunks = [];

      let receivedLength = 0;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
      }

      return new Blob(chunks);
    } catch (err) {
      console.warn(err);
    }
  }

  private async cacheVideo(url) {
    let blob;
    try {
      // blob = await fetch(url, { method: 'GET', mode: 'no-cors' }).then(res => res.blob());
      blob = await this.downloadLargeMkvToBlob(url);
      // const base64 = await this.blobToBase64(blob);
      let dataInDb;
      try {
        dataInDb = await this.db.put(url, blob);
        console.log('video in DB:', dataInDb);
      } catch (err) {
        console.warn('could not put item to video db', url, err);
      }
    } catch (err) {
      console.warn('could not get video with url: ', url, err);
    }
    let urlsInProgress = JSON.parse(localStorage.getItem('urlsInProgress'));
    if (urlsInProgress) {
      urlsInProgress.splice(urlsInProgress.findIndex(el => el === url), 1);
      localStorage.setItem('urlsInProgress', JSON.stringify(urlsInProgress));
    }
  }

  private getBase64(value: string): string {
    value = btoa(value);
    return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  base64ToBlob(base64, type) {
    const binary = atob(base64.split(',')[1]);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: type });
  }

}
